import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader2, AlertCircle, RefreshCw, Shield, Filter } from "lucide-react";

const _fmtWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const _eventBadge = (t) => {
  const cfg = {
    "auth.login.success":          { color: "#27ae60", icon: "✓" },
    "auth.login.failure":          { color: "#c0392b", icon: "✕" },
    "auth.login.locked":           { color: "#c0392b", icon: "⛔" },
    "admin.2fa.init.success":      { color: "#27ae60", icon: "→" },
    "admin.2fa.init.failure":      { color: "#c0392b", icon: "✕" },
    "admin.2fa.verify.success":    { color: "#27ae60", icon: "✓" },
    "admin.2fa.verify.failure":    { color: "#c0392b", icon: "✕" },
    "admin.snapshot_to_slack":     { color: "#4A154B", icon: "📣" },
    "admin.logout":                { color: "#888",    icon: "↩" },
  }[t] || { color: "#888", icon: "•" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: "999px",
        background: `${cfg.color}1A`,
        color: cfg.color,
        fontSize: "11px",
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.icon} {t.replace(/^admin\./, "").replace(/^auth\./, "")}
    </span>
  );
};

const AdminPlyxAudit = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState("");   // event_type filter

  const load = async (eventType = filter) => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/audit", { params: eventType ? { event_type: eventType } : {} });
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Forbidden");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/plyx/admin/login", { replace: true, state: { from: "/admin/plyx/audit" } });
      return;
    }
    if (user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  if (error) {
    return (
      <div className="container-narrow py-20 text-center text-muted-foreground" data-testid="audit-error">
        <AlertCircle className="w-8 h-8 mx-auto mb-4 opacity-60" />
        {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="container-narrow py-20 text-center" data-testid="audit-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto opacity-60" />
      </div>
    );
  }

  return (
    <div className="container-narrow py-10" data-testid="admin-plyx-audit-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs label-overline text-muted-foreground mb-2">
            <Shield className="w-3.5 h-3.5" /> Admin · audit log
          </div>
          <h1 className="font-display text-4xl">Who-did-what-when</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Append-only trail of admin sign-ins, 2FA challenges, snapshots and logouts. Last 200 rows.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/plyx"
            data-testid="audit-link-overview"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-black/10 text-sm hover:bg-black/5"
          >
            ← Overview
          </Link>
          <button
            onClick={() => load(filter)}
            disabled={refreshing}
            data-testid="audit-refresh-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-black text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap items-center gap-2 mb-6">
        <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
          <Filter className="w-3 h-3" /> filter:
        </span>
        <button
          data-testid="audit-filter-all"
          onClick={() => { setFilter(""); load(""); }}
          className={`text-xs px-2.5 py-1 rounded-full border ${filter === "" ? "bg-black text-white border-black" : "bg-white border-black/10 hover:bg-black/5"}`}
        >
          All ({Object.values(data.counts_7d || {}).reduce((s, n) => s + n, 0)})
        </button>
        {(data.event_types || []).map((t) => (
          <button
            key={t}
            data-testid={`audit-filter-${t}`}
            onClick={() => { setFilter(t); load(t); }}
            className={`text-xs px-2.5 py-1 rounded-full border ${filter === t ? "bg-black text-white border-black" : "bg-white border-black/10 hover:bg-black/5"}`}
          >
            {t.replace(/^admin\./, "").replace(/^auth\./, "")} ({(data.counts_7d || {})[t] || 0})
          </button>
        ))}
      </div>

      <div className="bg-white border border-black/10 rounded-xl overflow-hidden" data-testid="audit-table">
        <div className="px-6 py-3 border-b border-black/5 flex items-center justify-between">
          <span className="text-sm font-semibold">{data.events.length} event(s)</span>
          <span className="text-xs text-muted-foreground">7-day counts shown in filter chips above</span>
        </div>
        {data.events.length === 0 ? (
          <div className="px-6 py-10 text-center text-muted-foreground text-sm" data-testid="audit-empty">
            No events match this filter yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/5">
                <tr>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 200 }}>Event</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">Email</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 130 }}>IP</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground">Meta</th>
                  <th className="px-4 py-2 text-left text-xs uppercase tracking-wider text-muted-foreground" style={{ width: 180 }}>When</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => (
                  <tr key={e.event_id} className="border-t border-black/5" data-testid={`audit-row-${e.event_id}`}>
                    <td className="px-4 py-3">{_eventBadge(e.event_type)}</td>
                    <td className="px-4 py-3 text-xs">{e.email || <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{e.ip || "—"}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground" style={{ maxWidth: 320 }}>
                      <span className="truncate inline-block max-w-full" title={JSON.stringify(e.meta || {})}>
                        {Object.keys(e.meta || {}).length === 0 ? "—" : JSON.stringify(e.meta)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{_fmtWhen(e.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="text-xs text-muted-foreground mt-4">
        Generated at {_fmtWhen(data.generated_at)} · 9 event types tracked.
      </p>
    </div>
  );
};

export default AdminPlyxAudit;
